import axios from "axios";
import config from "../config.js";
import { Token } from "../utils/localStorage.util";

/**
 * Cliente para comunicação via Client Polling com a API PIS/COFINS
 * Substitui o SSE (Server-Sent Events) por requisições REST periódicas
 */
class ClientPollingManager {
  constructor() {
    this.baseURL = config.REACT_APP_HOST_API_PISCOFINS || config.REACT_APP_HOST_API_PLANEJAMENTO_TRIBUTARIO;
    this.pollingIntervals = new Map(); // Armazena intervalos ativos por versaoId
    this.pollingCallbacks = new Map(); // Armazena callbacks por versaoId
  }

  /**
   * Cria uma instância do axios com headers configurados
   */
  _createAxiosInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
    });

    // Adiciona interceptor para incluir token de autenticação
    instance.interceptors.request.use(
      (config) => {
        const token = Token.getToken();
        if (token) {
          config.headers.Authorization = token;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return instance;
  }

  /**
   * Cria uma nova versão e inicia a execução do motor
   * @param {Object} payload - Dados para criar a versão (idPrevia, descricao, tiposPreviaIds)
   * @returns {Promise<Object>} Resposta da API com dados da versão criada
   */
  async criarVersaoComMotor(payload) {
    try {
      const axiosInstance = this._createAxiosInstance();
      
      // A api-piscofins espera: idPrevia, descricao, tiposPreviaIds
      const normalizedPayload = {
        idPrevia: payload.idPrevia,
        descricao: payload.descricao || payload.descricaoVersao || 'original',
        tiposPreviaIds: payload.tiposPreviaIds || payload.idsTiposPrevia || [],
      };
      
      // Endpoint: POST /previas/pis-cofins/:idPrevia/versoes
      const response = await axiosInstance.post(
        `/previas/pis-cofins/${normalizedPayload.idPrevia}/versoes`,
        {
          descricao: normalizedPayload.descricao,
          tiposPreviaIds: normalizedPayload.tiposPreviaIds,
        }
      );
      
      // A resposta vem em response.data.dados (proteção contra resposta undefined em erro de rede)
      const versaoData = response?.data?.dados ?? response?.data ?? {};
      
      // Retornar no formato esperado pelo hook (com versaoId)
      return {
        versao: {
          id: versaoData.versaoId,
          versaoId: versaoData.versaoId,
        },
        versaoId: versaoData.versaoId,
        ...versaoData,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Executa um motor para uma versão existente
   * Na api-piscofins, a execução é automática após criar a versão
   * Este método existe apenas para compatibilidade com o hook
   * @param {number} versaoId - ID da versão
   * @returns {Promise<Object>} Resposta da API
   */
  async executarMotor(versaoId) {
    try {
      // A execução é automática após criar versão na api-piscofins
      // Retorna apenas o versaoId para compatibilidade
      return { versaoId };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Consulta o status de execução de uma versão (single request)
   * @param {number} versaoId - ID da versão
   * @returns {Promise<Object>} Status atual da execução
   */
  async acompanharProgresso(versaoId) {
    try {
      const axiosInstance = this._createAxiosInstance();
      // Endpoint: GET /versoes/:versaoId/status
      const response = await axiosInstance.get(
        `/versoes/${versaoId}/status`
      );
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Inicia polling automático para uma versão
   * @param {number} versaoId - ID da versão
   * @param {Function} onProgress - Callback chamado a cada atualização
   * @param {Function} onComplete - Callback chamado quando execução finaliza
   * @param {Function} onError - Callback chamado em caso de erro
   * @param {number} interval - Intervalo entre requisições em ms (padrão: 1000ms)
   */
  iniciarPolling(versaoId, onProgress, onComplete, onError, interval = 1000) {
    // Para polling anterior se existir
    this.pararPolling(versaoId);

    const intervalId = setInterval(async () => {
      try {
        const status = await this.acompanharProgresso(versaoId);
        
        // Trata status "aguardando" (primeira consulta antes do Redis estar pronto)
        if (status.aguardando) {
          if (onProgress) {
            onProgress(status);
          }
          return; // Continua polling
        }

        // Chama callback de progresso
        if (onProgress) {
          onProgress(status);
        }

        // Verifica se a execução foi finalizada
        if (status.concluido || status.erro) {
          this.pararPolling(versaoId);
          
          if (onComplete) {
            onComplete(status);
          }
        }
      } catch (error) {
        this.pararPolling(versaoId);
        
        if (onError) {
          onError(error);
        }
      }
    }, interval);

    this.pollingIntervals.set(versaoId, intervalId);
    this.pollingCallbacks.set(versaoId, { onProgress, onComplete, onError });
  }

  /**
   * Para o polling de uma versão específica
   * @param {number} versaoId - ID da versão
   */
  pararPolling(versaoId) {
    const intervalId = this.pollingIntervals.get(versaoId);
    if (intervalId) {
      clearInterval(intervalId);
      this.pollingIntervals.delete(versaoId);
      this.pollingCallbacks.delete(versaoId);
    }
  }

  /**
   * Para todos os pollings ativos
   */
  pararTodos() {
    this.pollingIntervals.forEach((intervalId, versaoId) => {
      clearInterval(intervalId);
    });
    this.pollingIntervals.clear();
    this.pollingCallbacks.clear();
  }

  /**
   * Desconecta e limpa todos os recursos
   */
  desconectar() {
    this.pararTodos();
  }
}

// Exporta uma instância singleton
export const clientPollingManager = new ClientPollingManager();

// Exporta a classe para casos de uso customizados
export default ClientPollingManager;
